import { contactFields } from "./form-fields";
import type { ContactField, ContactFieldValue, ContactSubmission } from "./types";

export type SubmissionValidation =
  | { ok: true; value: ContactSubmission }
  | { ok: false; fieldErrors: Record<string, string>; message: string };

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function validateValue(field: ContactField, rawValue: unknown): { value: ContactFieldValue; error?: string } {
  if (field.type === "checkbox") {
    const values = Array.isArray(rawValue)
      ? rawValue.filter((value): value is string => typeof value === "string").map((value) => value.trim())
      : [];

    if (field.required && values.length === 0) {
      return { value: values, error: `Select at least one ${field.label.toLowerCase()} option.` };
    }

    if (field.options && values.some((value) => !field.options?.includes(value))) {
      return { value: values, error: "Select only the available options." };
    }

    return { value: values };
  }

  const value = typeof rawValue === "string" ? rawValue.trim() : "";

  if (field.required && value.length === 0) {
    return { value, error: `${field.label} is required.` };
  }

  if (value && field.minLength && value.length < field.minLength) {
    return { value, error: `${field.label} must be at least ${field.minLength} characters.` };
  }

  if (field.maxLength && value.length > field.maxLength) {
    return { value, error: `${field.label} must be ${field.maxLength} characters or fewer.` };
  }

  if (field.type === "email" && value && !emailPattern.test(value)) {
    return { value, error: "Enter a valid email address." };
  }

  if ((field.type === "select" || field.type === "radio") && value && !field.options?.includes(value)) {
    return { value, error: "Select one of the available options." };
  }

  return { value };
}

export function validateSubmission(input: unknown, allowDevelopmentToken = false): SubmissionValidation {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, fieldErrors: {}, message: "The submission body is invalid." };
  }

  const candidate = input as Record<string, unknown>;
  const fieldErrors: Record<string, string> = {};
  const normalizedFields: Record<string, ContactFieldValue> = {};
  const rawFields =
    candidate.fields && typeof candidate.fields === "object" && !Array.isArray(candidate.fields)
      ? (candidate.fields as Record<string, unknown>)
      : {};

  const allowedKeys = new Set(contactFields.map((field) => field.key));
  for (const key of Object.keys(rawFields)) {
    if (!allowedKeys.has(key)) {
      fieldErrors[key] = "This field is not accepted.";
    }
  }

  for (const field of contactFields) {
    const result = validateValue(field, rawFields[field.key]);
    normalizedFields[field.key] = result.value;
    if (result.error) fieldErrors[field.key] = result.error;
  }

  const submissionId = typeof candidate.submissionId === "string" ? candidate.submissionId : "";
  if (!uuidPattern.test(submissionId)) {
    fieldErrors.submissionId = "The submission identifier is invalid.";
  }

  const website = typeof candidate.website === "string" ? candidate.website.trim().slice(0, 256) : "";
  const turnstileToken = typeof candidate.turnstileToken === "string" ? candidate.turnstileToken : "";
  const developmentTokenIsValid = allowDevelopmentToken && turnstileToken === "local-development";
  if ((!turnstileToken || turnstileToken.length > 2048) && !developmentTokenIsValid) {
    fieldErrors.turnstileToken = "Complete the security check and try again.";
  }

  if (Object.keys(fieldErrors).length > 0) {
    return { ok: false, fieldErrors, message: "Please check the highlighted fields." };
  }

  return {
    ok: true,
    value: { submissionId, fields: normalizedFields, turnstileToken, website },
  };
}
